import React from 'react';
import { Cloud, CloudRain, Sun, CloudSnow, Wind, Droplets, Thermometer } from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';

interface WeatherData {
  date: string;
  weather: 'sunny' | 'cloudy' | 'rainy' | 'snowy';
  temperature: number;
  humidity: number;
  impactCategories: {
    category: string;
    impact: number; // -100 to +100
    reason: string;
  }[];
}

const WeatherImpact: React.FC = () => {
  const weatherData: WeatherData[] = [
    {
      date: '2025-09-22',
      weather: 'sunny',
      temperature: 28,
      humidity: 60,
      impactCategories: [
        { category: '飲料', impact: 35, reason: '気温上昇により需要増' },
        { category: 'アイス', impact: 45, reason: '暑さによる購買意欲向上' },
        { category: '温かい食品', impact: -20, reason: '暑さにより需要減' }
      ]
    },
    {
      date: '2025-09-23',
      weather: 'rainy',
      temperature: 22,
      humidity: 85,
      impactCategories: [
        { category: '傘', impact: 80, reason: '雨天による需要急増' },
        { category: 'レインコート', impact: 65, reason: '雨対策商品の需要増' },
        { category: 'バーベキュー用品', impact: -60, reason: '屋外活動の減少' }
      ]
    },
    {
      date: '2025-09-24',
      weather: 'cloudy',
      temperature: 24,
      humidity: 70,
      impactCategories: [
        { category: '全般', impact: 0, reason: '平均的な需要' },
        { category: '季節商品', impact: 10, reason: '過ごしやすい気候' }
      ]
    },
    {
      date: '2025-09-25',
      weather: 'sunny',
      temperature: 26,
      humidity: 55,
      impactCategories: [
        { category: '飲料', impact: 25, reason: '晴天による外出増' },
        { category: 'レジャー用品', impact: 30, reason: '行楽日和' }
      ]
    }
  ];

  const getWeatherIcon = (weather: string) => {
    switch (weather) {
      case 'sunny': return <Sun size={24} style={{ color: '#FFA500' }} />;
      case 'cloudy': return <Cloud size={24} style={{ color: '#888' }} />;
      case 'rainy': return <CloudRain size={24} style={{ color: '#4169E1' }} />;
      case 'snowy': return <CloudSnow size={24} style={{ color: '#87CEEB' }} />;
      default: return <Cloud size={24} style={{ color: '#888' }} />;
    }
  };

  const getWeatherName = (weather: string) => {
    switch (weather) {
      case 'sunny': return '晴れ';
      case 'cloudy': return '曇り';
      case 'rainy': return '雨';
      case 'snowy': return '雪';
      default: return '曇り';
    }
  };

  const getImpactColor = (impact: number) => {
    if (impact > 30) return colors.support.success;
    if (impact > 0) return colors.accent.blue;
    if (impact < -30) return colors.status.error;
    if (impact < 0) return colors.status.warning;
    return colors.text.secondary;
  };

  return (
    <div className="space-y-6">
      {/* 週間天気予報と影響 */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text.primary }}>
          週間天気予報と需要への影響
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {weatherData.map((day, index) => (
            <div key={index} className="border rounded-lg p-4" style={{ borderColor: colors.ui.border }}>
              {/* 日付と天気 */}
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium" style={{ color: colors.text.primary }}>
                  {new Date(day.date).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric', weekday: 'short' })}
                </div>
                {getWeatherIcon(day.weather)}
              </div>
              
              {/* 天気情報 */}
              <div className="mb-3">
                <div className="text-xs" style={{ color: colors.text.secondary }}>
                  {getWeatherName(day.weather)}
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <div className="flex items-center gap-1">
                    <Thermometer size={14} style={{ color: colors.text.secondary }} />
                    <span className="text-sm">{day.temperature}°C</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Droplets size={14} style={{ color: colors.text.secondary }} />
                    <span className="text-sm">{day.humidity}%</span>
                  </div>
                </div>
              </div>
              
              {/* カテゴリ影響 */}
              <div className="space-y-2">
                {day.impactCategories.slice(0, 2).map((cat, idx) => (
                  <div key={idx} className="text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span style={{ color: colors.text.primary }}>{cat.category}</span>
                      <span style={{ color: getImpactColor(cat.impact) }}>
                        {cat.impact > 0 ? '+' : ''}{cat.impact}%
                      </span>
                    </div>
                    <div className="text-xs" style={{ color: colors.text.secondary }}>
                      {cat.reason}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* 気象パターン分析 */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <h4 className="font-semibold mb-3" style={{ color: colors.text.primary }}>
            気温と売上の相関
          </h4>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-2 rounded" style={{ backgroundColor: colors.ui.bgSecondary }}>
              <span className="text-sm">25°C以上</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium" style={{ color: colors.support.success }}>+15%</span>
                <Badge variant="success">飲料・アイス</Badge>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-2 rounded" style={{ backgroundColor: colors.ui.bgSecondary }}>
              <span className="text-sm">20-25°C</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium" style={{ color: colors.text.primary }}>±0%</span>
                <Badge variant="secondary">平均的</Badge>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-2 rounded" style={{ backgroundColor: colors.ui.bgSecondary }}>
              <span className="text-sm">20°C未満</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium" style={{ color: colors.support.success }}>+10%</span>
                <Badge variant="warning">温かい食品</Badge>
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <h4 className="font-semibold mb-3" style={{ color: colors.text.primary }}>
            降水確率の影響
          </h4>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-2 rounded" style={{ backgroundColor: colors.ui.bgSecondary }}>
              <span className="text-sm">降水確率80%以上</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium" style={{ color: colors.status.error }}>-20%</span>
                <Badge variant="error">来店客数</Badge>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-2 rounded" style={{ backgroundColor: colors.ui.bgSecondary }}>
              <span className="text-sm">雨天時</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium" style={{ color: colors.support.success }}>+50%</span>
                <Badge variant="success">雨具・傘</Badge>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-2 rounded" style={{ backgroundColor: colors.ui.bgSecondary }}>
              <span className="text-sm">台風接近</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium" style={{ color: colors.support.success }}>+80%</span>
                <Badge variant="warning">防災用品</Badge>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* アラート */}
      <Card className="p-4 border-l-4" style={{ borderLeftColor: colors.status.warning }}>
        <div className="flex items-start gap-3">
          <Wind size={20} style={{ color: colors.status.warning }} />
          <div className="flex-1">
            <h4 className="font-semibold mb-1" style={{ color: colors.text.primary }}>
              気象アラート
            </h4>
            <p className="text-sm" style={{ color: colors.text.secondary }}>
              今週木曜日から週末にかけて台風接近の可能性があります。防災用品・保存食の在庫を通常の150%確保することを推奨します。
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default WeatherImpact;
